import { Check } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"

// Tap-first input for the extras screens — chips over typing, one thumb.
// Single-select with tap-again-to-clear (every extra is skippable, so a chip
// must never be a trap).

export interface ChipOption<T extends string> {
  value: T
  label: string
}

export function ChipGroup<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: readonly ChipOption<T>[]
  value: T | null
  onChange: (value: T | null) => void
}) {
  return (
    <div role="radiogroup" aria-label={label}>
      <p className="mb-2 text-[14px] font-semibold text-ink">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const selected = opt.value === value
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(selected ? null : opt.value)}
              className={cx(
                "inline-flex min-h-11 items-center gap-1.5 rounded-full border px-4 py-2 text-[14px] font-medium transition-all duration-150",
                "outline-none focus-visible:ring-4 focus-visible:ring-emerald-500/25",
                selected
                  ? "border-emerald-500 bg-emerald-50/70 font-semibold text-emerald-900 shadow-[0_2px_8px_-4px_rgba(5,150,105,0.35)]"
                  : "border-[#D5DDD8] bg-white text-[#4A5A51] hover:border-emerald-300 hover:shadow-sm",
              )}
            >
              {selected && (
                <Check className="size-3.5 text-emerald-600" strokeWidth={3} aria-hidden />
              )}
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
